if (!customElements.get('sticky-atc-bar')) {
  class StickyAtcBar extends HTMLElement {
    constructor() {
      super()
      this.intersectionTarget = null
      this.resizeObserver = null
      this._hasIntersectionObservation = false
      this._relocating = false
      this._boundHide = this.hide.bind(this)
      this._boundReveal = this.reveal.bind(this)
      this._boundSyncPageOffset = this.syncPageOffset.bind(this)
    }

    connectedCallback() {
      // Escape stacking contexts (e.g. .product__info-container--sticky) and
      // survive form innerHTML swaps by living on document.body.
      if (this.parentElement !== document.body) {
        this._relocating = true
        document.body.appendChild(this)
        return
      }

      this._relocating = false

      document.querySelectorAll('sticky-atc-bar').forEach((el) => {
        if (el !== this) el.remove()
      })

      this.setup()
    }

    disconnectedCallback() {
      if (this._relocating) return
      this.teardown()
    }

    setup() {
      this.inert = !this.classList.contains('active')
      this.resolveIntersectionTarget()
      this.mainActionButtonBounds = false
      this._hasIntersectionObservation = false

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(this._boundSyncPageOffset)
        this.resizeObserver.observe(this)
      }

      this.createObserver()
      if (!this.intersectionTarget) {
        this.syncVisibility()
      }
      this.syncPageOffset()
    }

    teardown() {
      this.destroyObserver()
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      }
      this.clearPageOffset()
      this.intersectionTarget = null
      this.mainActionButtonBounds = false
      this._hasIntersectionObservation = false
    }

    /**
     * Move to document.body before form innerHTML swaps can destroy this host.
     */
    ensurePortaled() {
      if (this.parentElement === document.body) return
      this._relocating = true
      document.body.appendChild(this)
    }

    resolveIntersectionTarget() {
      const formId = this.dataset.formId
      let form = null
      if (formId) {
        const byId = document.getElementById(formId)
        form =
          byId instanceof HTMLFormElement
            ? byId
            : document.querySelector(`form#${CSS.escape(formId)}`)
      }
      this.intersectionTarget =
        form?.querySelector('.product-main-action-button') ?? null
    }

    /**
     * Re-bind after the product form is swapped on variant change without
     * remounting this element (preserves active visibility).
     */
    rebindIntersectionTarget() {
      this.destroyObserver()
      this.resolveIntersectionTarget()
      this.mainActionButtonBounds = false
      this._hasIntersectionObservation = false
      this.createObserver()
      if (!this.intersectionTarget) {
        this.syncVisibility()
      }
    }

    isStickyBarEnabled() {
      return true
    }

    destroyObserver() {
      if (this.observer) {
        this.observer.disconnect()
        this.observer = null
      }
      this._hasIntersectionObservation = false
    }

    createObserver() {
      if (!this.intersectionTarget) {
        return
      }

      this._hasIntersectionObservation = false
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.mainActionButtonBounds = entry.isIntersecting
        })
        this._hasIntersectionObservation = true
        this.syncVisibility()
      })
      this.observer.observe(this.intersectionTarget)

      // Apply pending records only when the browser already has them.
      // An empty takeRecords() after rAF must not sync — default bounds
      // are false and would flash-reveal before the real callback.
      if (typeof this.observer.takeRecords === 'function') {
        const records = this.observer.takeRecords()
        if (records.length) {
          records.forEach((entry) => {
            this.mainActionButtonBounds = entry.isIntersecting
          })
          this._hasIntersectionObservation = true
          this.syncVisibility()
        }
      }
    }

    syncVisibility() {
      if (!this.isStickyBarEnabled()) {
        this.hide()
        return
      }

      // Wait for the first real intersection result so we don't reveal
      // while mainActionButtonBounds is still the default false.
      if (this.intersectionTarget && !this._hasIntersectionObservation) {
        return
      }

      if (this.mainActionButtonBounds) {
        requestAnimationFrame(this._boundHide)
      } else {
        requestAnimationFrame(this._boundReveal)
      }
    }

    /**
     * Reserve space at the bottom of the document so fixed sticky ATC
     * does not permanently cover footer content at max scroll.
     */
    syncPageOffset() {
      if (!this.classList.contains('active') || !this.isStickyBarEnabled()) {
        this.clearPageOffset()
        return
      }

      const height = Math.ceil(this.getBoundingClientRect().height)
      document.documentElement.style.setProperty(
        '--sticky-atc-bar-offset',
        `${height}px`,
      )
      document.body.classList.add('has-sticky-atc-bar')
    }

    clearPageOffset() {
      document.body.classList.remove('has-sticky-atc-bar')
      document.documentElement.style.removeProperty('--sticky-atc-bar-offset')
    }

    hide() {
      if (this.classList.contains('active')) {
        this.classList.remove('active')
      }
      this.inert = true
      this.clearPageOffset()
    }

    reveal() {
      if (!this.classList.contains('active')) {
        this.classList.add('active')
      }
      this.inert = false
      this.syncPageOffset()
    }
  }

  customElements.define('sticky-atc-bar', StickyAtcBar)
}
